'use client'
import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { MoreCard, getPreviewImages } from '@/stories/organisms/CardList/MoreCard';
import styles from './card-grid.module.css';

export type CardGridProps = React.HTMLAttributes<HTMLDivElement> & {
  className?: string,
  /** nombre de cartes par page, carte « voir plus » comprise */
  pageSize?: number,
  /** libellé de la carte « voir plus » */
  moreLabel?: string,
  /** une image par carte, dans l'ordre des enfants : alimente l'aperçu en éventail */
  images?: (string | undefined)[]
}

/** grille paginée : la carte « voir plus » occupe la dernière place et ajoute une page de cartes */
export const CardGrid = ({className, children, pageSize = 8, moreLabel = 'Voir plus', images, ...props}: CardGridProps) => {
  const [pages, setPages] = useState(1)
  // première carte ajoutée par « voir plus », qui reçoit le focus (la carte « voir plus » a pu disparaître)
  const firstNewRef = useRef<HTMLDivElement>(null)
  const [firstNewIndex, setFirstNewIndex] = useState<number | null>(null)

  const items = React.Children.toArray(children)
  const limit = pages * pageSize
  const hasMore = items.length > limit
  const visibleItems = items.slice(0, hasMore ? limit - 1 : limit)

  useEffect(() => {
    firstNewRef.current?.focus()
  }, [firstNewIndex])

  const showMore = () => {
    setFirstNewIndex(visibleItems.length)
    setPages(pages + 1)
  }

  return (
    <div className={clsx(styles.grid, className)} {...props}>
      {visibleItems.map((child, i) => (
        <div
          className={styles.item}
          key={React.isValidElement(child) ? child.key : undefined}
          {...(i === firstNewIndex ? {ref: firstNewRef, tabIndex: -1} : {})}>
          {child}
        </div>
      ))}
      {hasMore && (
        <div className={styles.item}>
          <MoreCard onClick={showMore} label={moreLabel} images={getPreviewImages(images, visibleItems.length)} fluid/>
        </div>
      )}
    </div>
  )
}
